import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "lib/utils"
import { requestTracker } from "lib/requestTracker"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-br from-primary to-accent text-primary-foreground shadow hover:from-primary/90 hover:to-accent/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

// How long to wait for a click to start an API call before dropping the loader.
const NO_REQUEST_GRACE_MS = 400
const MAX_LOADING_MS = 30000

const Button = React.forwardRef(({ className, variant, size, asChild = false, loading: loadingProp, onClick, disabled, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  const [clickLoading, setClickLoading] = React.useState(false)
  const mounted = React.useRef(true)
  const cleanup = React.useRef(null)

  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      cleanup.current?.()
    }
  }, [])

  const handleClick = (e) => {
    const result = onClick?.(e)
    if (asChild || (e?.defaultPrevented && !result?.then)) return

    const startSeq = requestTracker.getStarted()
    let done = false
    const timers = []
    let unsubscribe = () => {}
    const finish = () => {
      if (done) return
      done = true
      timers.forEach(clearTimeout)
      unsubscribe()
      cleanup.current = null
      if (mounted.current) setClickLoading(false)
    }
    cleanup.current = finish
    // Deferred so a submit button isn't disabled before the browser submits its form.
    timers.push(setTimeout(() => { if (!done && mounted.current) setClickLoading(true) }, 0))

    if (result && typeof result.then === "function") {
      // Async handler: loader lasts until the promise settles.
      result.then(finish, finish)
    } else {
      // Sync handler (e.g. dispatching a thunk or form submit): follow the API calls it triggers.
      unsubscribe = requestTracker.subscribe(() => {
        if (requestTracker.getPending() === 0 && requestTracker.getStarted() > startSeq) finish()
      })
      timers.push(setTimeout(() => {
        if (requestTracker.getStarted() === startSeq) finish()
      }, NO_REQUEST_GRACE_MS))
    }
    timers.push(setTimeout(finish, MAX_LOADING_MS))
  }

  const isLoading = !!loadingProp || clickLoading
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      onClick={handleClick}
      {...props}>
      {asChild ? children : (
        <>
          {isLoading && <Loader2 className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
